import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import MobileBottomBar from '../../components/feature/MobileBottomBar';
import QuoteForm from '../../components/base/QuoteForm';
import { useEffect } from 'react';
import { useSEO } from '../../utils/seo';

const HouseRemovalsPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://danvanremovals.co.uk';
  
  useSEO({
    title: 'House Removals Lancashire | Professional Home Moving | Dan & Van',
    description: 'Stress-free house removals across Lancashire. Fully insured, professional packing, careful handling. From studio flats to 5-bed houses. Free quote — 07530 166035',
    keywords: 'house removals Lancashire, home moving, residential removals, house movers, packing service, furniture removal',
    canonical: '/house-removals',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      serviceType: 'House Removals',
      provider: {
        '@type': 'MovingCompany',
        name: 'Dan & Van Removals',
        telephone: '07530166035',
        areaServed: 'Lancashire, UK',
      },
      description: 'Professional house removals service across Lancashire. We handle everything from careful packing to safe transportation, ensuring your belongings arrive at your new home in perfect condition.',
      offers: {
        '@type': 'Offer',
        availability: 'https://schema.org/InStock',
        priceRange: '££',
      },
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const features = [
    {
      icon: 'ri-home-smile-line',
      title: 'Full House Moves',
      description: 'Complete packing, loading, and transportation service',
    },
    {
      icon: 'ri-box-3-line',
      title: 'Packing Service',
      description: 'Professional packing materials and expert wrapping',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Fully Insured',
      description: 'Comprehensive goods-in-transit insurance included',
    },
    {
      icon: 'ri-time-line',
      title: 'Flexible Timing',
      description: 'Weekend and evening moves available',
    },
  ];

  const process = [
    {
      step: '1',
      title: 'Free Quote',
      description: 'Contact us with your moving details for an instant quote',
    },
    {
      step: '2',
      title: 'Pre-Move Survey',
      description: 'We assess your belongings and plan the logistics',
    },
    {
      step: '3',
      title: 'Packing Day',
      description: 'Optional professional packing service available',
    },
    {
      step: '4',
      title: 'Moving Day',
      description: 'Careful loading, transport, and unloading at your new home',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <MobileBottomBar />

      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden mt-20">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://static.readdy.ai/image/ab22404d73908d3c144c597ec26057d7/2bda0e4b0cf57d6db6ac67fb518ad6d8.png')`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-gold text-xs uppercase tracking-wider mb-4"
          >
            <Link to="/" className="hover:underline cursor-pointer">Home</Link> / Services / House Removals
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="font-sans text-6xl font-bold mb-6"
          >
            House Removals
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-xl text-white/90 mb-8"
          >
            Professional home moving service across Lancashire
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <Link
              to="/contact"
              className="inline-block px-10 py-4 bg-gold text-charcoal font-semibold text-lg rounded hover:shadow-luxury-hover transition-all whitespace-nowrap cursor-pointer"
            >
              Get a Quote
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <h2 className="font-sans text-4xl font-bold text-charcoal mb-6">
                Stress-free house removals in Lancashire
              </h2>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Moving house is one of life's most stressful events, but it doesn't have to be. At Dan & Van Removals, we specialise in making your house move as smooth and stress-free as possible. Our experienced team handles everything from careful packing to safe transportation, ensuring your belongings arrive at your new home in perfect condition.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                Whether you're moving from a studio flat or a five-bedroom house, we have the expertise and equipment to handle moves of any size across Lancashire and beyond.
              </p>

              <h3 className="font-sans text-3xl font-bold text-charcoal mb-6 mt-12">
                What's included in our house removals service
              </h3>

              <ul className="space-y-4 mb-12">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-gold text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">
                    <strong className="text-charcoal">Professional packing materials:</strong> High-quality boxes, bubble wrap, protective blankets, and tape
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-gold text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">
                    <strong className="text-charcoal">Careful loading and unloading:</strong> Trained team using proper lifting techniques
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-gold text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">
                    <strong className="text-charcoal">Safe transportation:</strong> Modern, well-maintained vehicles with secure tie-down systems
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-gold text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">
                    <strong className="text-charcoal">Full insurance cover:</strong> Comprehensive goods-in-transit and public liability insurance
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-gold text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">
                    <strong className="text-charcoal">Furniture disassembly and reassembly:</strong> We'll take apart and rebuild beds, wardrobes, and more
                  </span>
                </li>
              </ul>

              <h3 className="font-sans text-3xl font-bold text-charcoal mb-6">
                Our house removal process
              </h3>

              <div className="space-y-6 mb-12">
                {process.map((item, index) => (
                  <div key={index} className="flex items-start">
                    <div className="flex-shrink-0 w-12 h-12 bg-gold rounded-full flex items-center justify-center mr-4">
                      <span className="font-sans text-xl font-bold text-charcoal">
                        {item.step}
                      </span>
                    </div>
                    <div>
                      <h4 className="font-sans text-xl font-bold text-charcoal mb-2">
                        {item.title}
                      </h4>
                      <p className="text-gray-700">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <h3 className="font-sans text-3xl font-bold text-charcoal mb-6">
                Why choose Dan & Van for your house move?
              </h3>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                We understand that your home contains your most precious belongings and memories. That's why we treat every item with the utmost care and respect. Our team is fully trained in proper handling techniques, and we use professional-grade protective equipment to ensure nothing gets damaged during the move.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                With years of experience serving Lancashire homeowners, we know the local area inside out. We can navigate narrow streets, handle parking restrictions, and work efficiently to get you settled into your new home as quickly as possible.
              </p>

              <div className="bg-gray-50 border-l-4 border-gold p-6 rounded">
                <p className="text-lg text-charcoal font-medium mb-2">
                  Ready to book your house removal?
                </p>
                <p className="text-gray-700 mb-4">
                  Get your free, no-obligation quote today. We'll respond within 24 hours.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="tel:07530166035"
                    className="px-6 py-3 border-2 border-charcoal text-charcoal font-semibold rounded hover:border-gold hover:text-gold transition-all text-center whitespace-nowrap cursor-pointer"
                  >
                    Call 07530 166035
                  </a>
                  <Link
                    to="/contact"
                    className="px-6 py-3 bg-gold text-charcoal font-semibold rounded hover:shadow-luxury-hover transition-all text-center whitespace-nowrap cursor-pointer"
                  >
                    Get a Quote
                  </Link>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <div className="bg-white border border-gold/20 rounded-lg p-8 shadow-luxury mb-8">
                  <h3 className="font-sans text-2xl font-bold text-charcoal mb-6">
                    Get Your Free Quote
                  </h3>
                  <p className="text-gray-700 mb-6">
                    Contact us directly for a fast, free quote on your house removal.
                  </p>
                  <div className="space-y-3">
                    <a
                      href="https://wa.me/447530166035"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center w-full py-3 bg-green-600 text-white font-semibold rounded hover:bg-green-700 transition-all whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-whatsapp-line mr-2 text-xl"></i>
                      WhatsApp Us
                    </a>
                    <a
                      href="tel:07530166035"
                      className="flex items-center justify-center w-full py-3 border-2 border-charcoal text-charcoal font-semibold rounded hover:border-gold hover:text-gold transition-all whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-phone-line mr-2 text-lg"></i>
                      Call Now
                    </a>
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-4">
                  {features.map((feature, index) => (
                    <div
                      key={index}
                      className="bg-gray-50 p-4 rounded-lg flex items-start"
                    >
                      <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 mr-3">
                        <i className={`${feature.icon} text-2xl text-gold`}></i>
                      </div>
                      <div>
                        <h4 className="font-sans font-bold text-charcoal mb-1">
                          {feature.title}
                        </h4>
                        <p className="text-sm text-gray-600">{feature.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <h3 className="font-sans text-3xl font-bold text-charcoal mb-8 text-center">
            Other services you might need
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link
              to="/office-removals"
              className="group relative h-64 rounded-lg overflow-hidden cursor-pointer"
            >
              <div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-300 group-hover:scale-110"
                style={{
                  backgroundImage: `url('https://public.readdy.ai/ai/img_res/74399be7798ed6440d576ccacd791a86.jpg')`,
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/20"></div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <h4 className="font-sans text-2xl font-bold mb-2">Office Removals</h4>
                <span className="text-gold flex items-center">
                  Learn more
                  <i className="ri-arrow-right-line ml-2"></i>
                </span>
              </div>
            </Link>

            <Link
              to="/single-item-moves"
              className="group relative h-64 rounded-lg overflow-hidden cursor-pointer"
            >
              <div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-300 group-hover:scale-110"
                style={{
                  backgroundImage: `url('https://readdy.ai/api/search-image?query=single%20large%20furniture%20piece%20sofa%20being%20carefully%20moved%20by%20professional%20movers%20with%20protective%20wrapping%20and%20equipment%20simple%20clean%20background&width=800&height=600&seq=single002&orientation=landscape')`,
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/20"></div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <h4 className="font-sans text-2xl font-bold mb-2">Single Item Moves</h4>
                <span className="text-gold flex items-center">
                  Learn more
                  <i className="ri-arrow-right-line ml-2"></i>
                </span>
              </div>
            </Link>

            <Link
              to="/collections-deliveries"
              className="group relative h-64 rounded-lg overflow-hidden cursor-pointer"
            >
              <div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-300 group-hover:scale-110"
                style={{
                  backgroundImage: `url('https://readdy.ai/api/search-image?query=delivery%20van%20with%20parcels%20and%20boxes%20being%20loaded%20professional%20courier%20service%20clean%20modern%20vehicle%20simple%20background&width=800&height=600&seq=delivery002&orientation=landscape')`,
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/20"></div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <h4 className="font-sans text-2xl font-bold mb-2">Collections & Deliveries</h4>
                <span className="text-gold flex items-center">
                  Learn more
                  <i className="ri-arrow-right-line ml-2"></i>
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HouseRemovalsPage;
